import {
  CreateResourceInput,
  IDSK,
  Replace,
  Resource,
  SearchableResourceConnectionNew,
} from "@examspark/types";

import { GraphQLAPI } from "../../client";
import { parseSearchResults } from "../../deserializers";
import * as fragments from "../../fragments/resource";
import { exam, search as searchQueries } from "../../queries";
import { DuplicatedExamConfigData } from "../../types";
import { getResource, search, updateResource } from "../resource";

export const pauseExamConfig = async (params: Partial<Resource>) => {
  return await updateResource({
    id: params.id,
    sk: params.sk,
    maturity: "PAUSED",
  });
};

export const resumeExamConfig = async (params: Partial<Resource>) => {
  return await updateResource({
    id: params.id,
    sk: params.sk,
    maturity: "IN_PROGRESS",
  });
};

export const archiveExamConfig = async (params: Partial<Resource>) => {
  return await updateResource({
    id: params.id,
    sk: params.sk,
    isArchived: true,
  });
};

export const cancelExamConfig = async (params: Partial<Resource>) => {
  return await updateResource({
    id: params.id,
    sk: params.sk,
    maturity: "CANCELLED",
  });
};

export const finalizeExamConfig = async (params: Partial<Resource>) => {
  return await updateResource({
    id: params.id,
    sk: params.sk,
    maturity: "COMPLETE_NEEDS_SCORING",
  });
};

export const searchExamConfigForEmptyResponseScores = async (
  examConfigId: string,
) => {
  console.log("missing results", examConfigId);
  const results = await GraphQLAPI.client.query<{
    search: SearchableResourceConnectionNew;
  }>({
    query: searchQueries.search({
      label: "SearchExamConfigForEmptyResponseScores",
      fields: `
        id
        sk
        scoreMin
        scoreMax
        scoreActual
      `,
    }),
    variables: {
      filter: {
        examConfigId: {
          eq: examConfigId,
        },
        scoreActual: {
          exists: false,
        },
      },
      aggsNew: [
        {
          name: "sk",
          type: "terms",
          field: "sk",
        },
      ],
    },
  });

  return parseSearchResults(results.data?.search);
};

export const getExamConfig = async ({
  id,
  sk,
}: Replace<Partial<IDSK>, { sk: "exam-config" }>) => {
  if (!id) return null;

  const result = await GraphQLAPI.query<{ getExamConfig: Resource }>({
    query: exam.getExamConfigQuery,
    variables: {
      input: {
        id,
        sk,
      },
    },
  });

  return result.data?.getExamConfig;
};

export const getExamConfigProperties = async (params: IDSK) => {
  return (
    await getResource({
      label: "GetExamConfigProperties",
      variables: params,
      fields: `
      ${fragments.id}
      ${fragments.basic}
      ${fragments.alignments}
      resource {
        ${fragments.id}
        ${fragments.basic}
        ${fragments.meta}
      }
    `,
    })
  ).data?.getResource;
};

export const getExamConfigForDuplication = async (params: IDSK) => {
  return (
    await getResource<DuplicatedExamConfigData>({
      label: "GetExamConfigProperties",
      variables: params,
      fields: `
      ${fragments.id}
      ${fragments.basic}
      ${fragments.alignments}
      ${fragments.tags}
      alignmentIds
      creatorId
      appId
      appKey
      resource {
        id
        sk
        name
        description
      }
    `,
    })
  ).data?.getResource;
};

export const searchExamConfigsForExam = async (params: {
  examId: string;
  workspaceId: string;
}) => {
  return (
    await search({
      label: "SearchExamConfigsForExam",
      fields: `
        ${fragments.id}
        ${fragments.basic}
        ${fragments.tags}
    `,
      variables: {
        filter: {
          resourceId: {
            eq: `${params.examId}/exam#v0`,
          },
          workspaceId: {
            matchPhrasePrefix: params.workspaceId,
          },
          isDeleted: {
            ne: true,
          },
          isArchived: {
            ne: true,
          },
        },
      },
    })
  ).data?.search;
};

export const createExamConfigSession = async (
  params: Replace<CreateResourceInput, { sk: `exam-config-session#${string}` }>,
) => {
  return GraphQLAPI.client
    .query<{ createExamConfigSession: IDSK }>({
      query: /* GraphQL */ `
        mutation CreateExamConfigSession($input: CreateResourceInput!) {
          createExamConfigSession(input: $input) {
            id
            sk
          }
        }
      `,
      variables: {
        input: params,
      },
      authMode: "API_KEY",
    })
    .then((res) => res?.data?.createExamConfigSession);
};

export const getExamConfigResults = async (examConfigId: string) => {
  return GraphQLAPI.client.query<{
    getResource: Resource;
  }>({
    query: /* GraphQL */ `
      query GetResource($id: ID!, $sk: String!) {
        getResource(id: $id, sk: $sk) {
          id
          sk
          idInc
          name
          maturity
          ${fragments.alignments}
          resource {
            id
            sk
            name
            description
            createdAt
            grade
            difficulty
            workspace {
              id
              sk
              workspaceId
              schemaVersion
              name
            }
            content {
              forms {
                sections {
                  questionIds
                }
              }
            }
          }
          workspaceId
          createdAt
          updatedAt
        }
      }
    `,
    variables: {
      id: examConfigId,
      sk: "exam-config",
    },
  });
};
